/* eslint-disable no-underscore-dangle */
import { computed, inject, readonly, ref } from 'vue';
import {
  External_Authentication_Type,
  License_RegistrationType,
  QUERY_RESULT_PATH,
  Settings_Port_Type,
  Settings_SetRequest,
  useLicenseAddonAndProductQuery,
  useSettings_ChangeCoordinatorPort_AsyncMutation,
  useSettings_ChangeManagerPortMutation,
  useSettings_CheckPort_AsyncMutation,
  useSettings_Update_AsyncMutation,
  useSettingsPageLinuxQuery,
  useSettingsPageWindowsQuery,
} from '@/graphql/codegen/generated/graphql';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { inputWithMenuFilter } from '@/plugins/filters';
import { isNumberInRange, onlyPositiveOrZeroNumbers } from '@/services/utils';
import { longPolling } from '@/services/PlatfromGQL/longPolling';
import { IKCoordinatorsService } from '@/modules/CoordinatorsService/CoordinatorService.InjectionKeys';
import { useDialogPresets } from '@/services/dialogPresets.composable';
import { useNotify } from '@/services/notify/notify';
import { CONST_NOTIFY } from '@/services/notify/CONST';
import { useHealthCheckService } from '@/modules/healthCheckService/healthCheck.helpers';
import { IKPlatformAuthentication } from '@/modules/platformAuthentication/platformAuthentication.injectionKeys';
import { IKHealthCheck } from '@/modules/healthCheckService/healthCheck.InjectionKeys';
import { refreshPageAfterUIPortChange } from '@/views/settings/util';
import { isBuildAvoidanceLicensed } from '@/globals/functions/licenseValidationRules';
import { useApiAccess } from '@/views/settings/settingsApiAccess/settingsApiAccess.helpers';
import { setInitiatorType } from '@/views/settings/components/settingsDefaultAgentLicense/settings-default-agent-license.helpers';
import { ActiveDirectoryStatus } from '@/views/settings/const';
import { useOsService } from '@/services/OsService/OsService';
import { InputsDirty, LinuxValidationsType, SettingsKeysHandledByRest, SettingsResultPaths, ValidPortsType } from './settings.types';
import { ResponseStatus } from '../../const/globals';

export const useSettingsErrorMessages = () => {
  const { t } = useI18n();
  const getPortRangeValidationErrorMessage = (val: number) => {
    if (isNumberInRange(0, 1024, val) || val > 65535) {
      return t('settings.general.network.portsRangeValueErrorMessage');
    }
    return t('settings.general.network.portsAvailabilityValidationErrorMessage');
  };

  const getPositivePortValidationErrorMessage = (val: number) => {
    if (!onlyPositiveOrZeroNumbers(val) || val > 65535) {
      return t('settings.general.network.portsPositiveRangeValueErrorMessage');
    }
  };

  return {
    getPortRangeValidationErrorMessage,
    getPositivePortValidationErrorMessage,
  };
};

export const useValidPorts = () => {
  const { mutate: GQL_MUTATE_checkPort } = useSettings_CheckPort_AsyncMutation({});
  const osService = useOsService();

  const PORT_KEYS = {
    ibManagerPort: QUERY_RESULT_PATH.settings.common.ibManager.ibManagerPort,
    restServicePort: QUERY_RESULT_PATH.settings.windows.coordinator.restServicePort,
    licenseServerPort: QUERY_RESULT_PATH.settings.windows.license.licenseServerPort,
    agentCommunicationSSLPort: QUERY_RESULT_PATH.settings.windows.coordinator.agentCommunicationSSLPort,
    agentCommunicationPort: QUERY_RESULT_PATH.settings.windows.coordinator.agentCommunicationPort,
    prometheusPort: QUERY_RESULT_PATH.settings.windows.system.prometheusPort,
  };

  const runningLongPollingQuries = ref<Map<keyof ValidPortsType, string>>(new Map());
  const validPorts = ref<ValidPortsType>({
    [QUERY_RESULT_PATH.settings.windows.coordinator.agentCommunicationPort]: {
      isValid: null,
      port: null,
      isFetching: false,
    },
    [QUERY_RESULT_PATH.settings.windows.coordinator.agentCommunicationSSLPort]: {
      isValid: null,
      port: null,
      isFetching: false,
    },
    [QUERY_RESULT_PATH.settings.windows.coordinator.restServicePort]: {
      isValid: null,
      port: null,
      isFetching: false,
    },
    [QUERY_RESULT_PATH.settings.common.ibManager.ibManagerPort]: {
      isValid: null,
      port: null,
      isFetching: false,
    },
    [QUERY_RESULT_PATH.settings.windows.license.licenseServerPort]: {
      isValid: null,
      port: null,
      isFetching: false,
    },
    [QUERY_RESULT_PATH.settings.windows.system.prometheusPort]: {
      isValid: null,
      port: null,
      isFetching: false,
    },
  });

  const fetchingCounters = ref<Record<keyof ValidPortsType, number>>({
    [QUERY_RESULT_PATH.settings.windows.coordinator.agentCommunicationPort]: 0,
    [QUERY_RESULT_PATH.settings.windows.coordinator.agentCommunicationSSLPort]: 0,
    [QUERY_RESULT_PATH.settings.windows.coordinator.restServicePort]: 0,
    [QUERY_RESULT_PATH.settings.common.ibManager.ibManagerPort]: 0,
    [QUERY_RESULT_PATH.settings.windows.license.licenseServerPort]: 0,
    [QUERY_RESULT_PATH.settings.windows.system.prometheusPort]: 0,
  });

  const setValidPorts = (key: keyof ValidPortsType, value: { isValid: null | boolean; port: null | number; isFetching: boolean }) => {
    validPorts.value[key] = value;
  };
  const validatePort = async (key: keyof ValidPortsType, value: number) => {
    const mapPortKey: Record<typeof key, Settings_Port_Type> = {
      'settings.common.ibManager.ibManagerPort': Settings_Port_Type.ManagerPort,
      'settings.windows.coordinator.restServicePort': Settings_Port_Type.CoordinatorPort,
      'settings.windows.license.licenseServerPort': Settings_Port_Type.LicensePort,
      'settings.windows.coordinator.agentCommunicationPort': Settings_Port_Type.AgentPort,
      'settings.windows.system.prometheusPort': Settings_Port_Type.CoordinatorPort,
      'settings.windows.coordinator.agentCommunicationSSLPort': Settings_Port_Type.AgentPort,
    };
    try {
      const currentFetchingCounter = ++fetchingCounters.value[key];
      if (osService.isWindows.value && isNumberInRange(0, 1024, value)) {
        setValidPorts(key, { isValid: false, port: value, isFetching: false });
        return !!validPorts.value[key].isValid;
      }
      setValidPorts(key, { isValid: null, port: null, isFetching: true });
      const status = await GQL_MUTATE_checkPort({
        data: {
          port: value,
          portType: mapPortKey[key],
        },
      });
      runningLongPollingQuries.value.set(key, status?.data?.settings_checkPort_async);
      const result = await longPolling.fetch('settings_checkPort_asyncResult', status?.data?.settings_checkPort_async);
      if (currentFetchingCounter === fetchingCounters.value[key]) {
        setValidPorts(key, {
          isValid: result.data.settings_checkPort_asyncResult?.result?.status === ResponseStatus.Ok,
          port: result.data.settings_checkPort_asyncResult?.result?.port || null,
          isFetching: false,
        });
      }
      return !!validPorts.value[key].isValid;
    } catch (error) {
      return false;
    }
  };
  const resetValidationFor = (key: keyof ValidPortsType) => {
    const longPollingId = runningLongPollingQuries.value.get(key);
    if (longPollingId) longPolling.abort(longPollingId);
    fetchingCounters.value[key] = 0;
    setValidPorts(key, { isValid: null, port: null, isFetching: false });
  };
  return { PORT_KEYS, validPorts: readonly(validPorts), resetValidationFor, validatePort };
};

export const useLinuxInputsValidations = () => {
  const validInputs = ref<LinuxValidationsType>({
    [QUERY_RESULT_PATH.settings.linux.coordinator.agentMaxCpuLoad]: {
      isValid: null,
    },
    [QUERY_RESULT_PATH.settings.linux.coordinator.agentMinFreeMemory]: {
      isValid: null,
    },
    [QUERY_RESULT_PATH.settings.linux.coordinator.agentReloadTimeout]: {
      isValid: null,
    },
    [QUERY_RESULT_PATH.settings.common.backup.backupCoordinatorTimeout]: {
      isValid: null,
    },
    [QUERY_RESULT_PATH.settings.common.backup.backupCoordinatorSyncFrequency]: {
      isValid: null,
    },
    [QUERY_RESULT_PATH.settings.linux.network.coordinatorPort]: {
      isValid: null,
    },
    [QUERY_RESULT_PATH.settings.linux.network.coordinatorSSLPort]: {
      isValid: null,
    },
  });

  const VALIDATION_KEYS = {
    agentMaxCpuLoad: QUERY_RESULT_PATH.settings.linux.coordinator.agentMaxCpuLoad,
    agentMinFreeMemory: QUERY_RESULT_PATH.settings.linux.coordinator.agentMinFreeMemory,
    agentReloadTimeout: QUERY_RESULT_PATH.settings.linux.coordinator.agentReloadTimeout,
    backupCoordinatorTimeout: QUERY_RESULT_PATH.settings.common.backup.backupCoordinatorTimeout,
    backupCoordinatorSyncFrequency: QUERY_RESULT_PATH.settings.common.backup.backupCoordinatorSyncFrequency,
    coordinatorPort: QUERY_RESULT_PATH.settings.linux.network.coordinatorPort,
    coordinatorSSLPort: QUERY_RESULT_PATH.settings.linux.network.coordinatorSSLPort,
  };

  const setValidInput = (key: keyof LinuxValidationsType, value: { isValid: null | boolean }) => {
    validInputs.value[key] = value;
  };

  const resetLinuxValidation = (key: keyof LinuxValidationsType) => {
    setValidInput(key, { isValid: null });
  };

  const validateLinuxPorts = (key: keyof LinuxValidationsType, value: number | string, portToCompare: number | string) => {
    const isSamePort = Number(value) === Number(portToCompare);
    setValidInput(key, { isValid: onlyPositiveOrZeroNumbers(value) && !isSamePort });
  };

  const validateLinuxInput = async (key: keyof LinuxValidationsType, value: number | string) => {
    switch (key) {
      case QUERY_RESULT_PATH.settings.linux.coordinator.agentMaxCpuLoad:
        setValidInput(key, { isValid: onlyPositiveOrZeroNumbers(value) && isNumberInRange(0, 100, Number(value)) });
        break;
      case QUERY_RESULT_PATH.settings.linux.coordinator.agentMinFreeMemory:
        setValidInput(key, { isValid: onlyPositiveOrZeroNumbers(value) });
        break;
      case QUERY_RESULT_PATH.settings.linux.coordinator.agentReloadTimeout:
        setValidInput(key, { isValid: onlyPositiveOrZeroNumbers(value) });
        break;
      case QUERY_RESULT_PATH.settings.common.backup.backupCoordinatorTimeout:
        setValidInput(key, { isValid: onlyPositiveOrZeroNumbers(value) && Number(value) >= 30 });
        break;
      case QUERY_RESULT_PATH.settings.common.backup.backupCoordinatorSyncFrequency:
        setValidInput(key, { isValid: onlyPositiveOrZeroNumbers(value) });
        break;
      default:
        break;
    }
  };

  return { validLinuxInputs: validInputs, validateLinuxInput, LINUX_VALIDATION_KEYS: VALIDATION_KEYS, resetLinuxValidation, validateLinuxPorts };
};
// Enterprise Tier license
export const useLicenseFeatures = () => {
  const coordinatorsService = inject(IKCoordinatorsService);
  const GQL_userLicenseAddonAndProduct = useLicenseAddonAndProductQuery(() => ({
    coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
  }));
  const isLicenseContainAPIAccessFeature = computed(() => {
    const features = GQL_userLicenseAddonAndProduct?.result?.value?.ibManagerEntities?.license?.windows?.features?.productFeatures;
    return features?.includes('API Access');
  });
  return { isLicenseContainAPIAccessFeature };
};

export const useSettingsSummaryWindows = () => {
  const coordinatorsService = inject(IKCoordinatorsService);
  const { isLicenseContainAPIAccessFeature } = useLicenseFeatures();
  const { result } = useSettingsPageWindowsQuery(() => ({
    coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
  }));
  const { GQL_useKeysQuery } = useApiAccess();
  const activeAPIKeys = computed(() => GQL_useKeysQuery?.result?.value?.keys || []);
  const shouldHideReleaseOfflineAgents = computed(
    () => result.value?.ibManagerEntities?.license?.windows?.summary?.totalFloatingCIInitiators === 0 && result.value?.ibManagerEntities?.license?.windows?.summary?.totalFloatingInitiators === 0,
  );
  const adStatus = computed(() => {
    const enabled = result.value?.ibManagerEntities?.settings?.common?.auth?.enabled;
    if (enabled) {
      return result.value?.ibManagerEntities?.settings?.common?.auth?.authenticationType === External_Authentication_Type.Ldap ? ActiveDirectoryStatus.adEnabled : ActiveDirectoryStatus.azureADEnabled;
    }
    return ActiveDirectoryStatus.disabled;
  });
  const store = useStore();
  const inputsWithNumMap: any = {
    DAYS: ' DAYS',
    MON: ' MON',
    SEC: ' SEC',
    MIN: ' MIN',
    HRS: ' HRS',
    MB: ' MB',
    GB: ' GB',
    TB: ' TB',
  };
  const inputsSelectedOption = computed(() => {
    const curOptionSelected = store?.state?.settings?.inputsWithMenuSelectedOption || {};
    return {
      TelemetryDBMaxSize: curOptionSelected['telemetry-db-max-size-component'],
      TelemetryDaysToSave: curOptionSelected['telemtry-days-to-save-component'],
      MinDiskFree: curOptionSelected['disk-space-component'],
      MinVirtualMem: curOptionSelected['min-virtual-memory-component'],
      MinPhysicalMem: curOptionSelected['min-physical-memory-component'],
      FloatingReleaseTime: curOptionSelected['floating-release-time-component'],
      CacheCleanupInterval: curOptionSelected['time-period-space-component'],
      ReportInterval: curOptionSelected['report-interval-component'],
      OfflineAgentsPeriod: curOptionSelected['offline-agents-component'],
    };
  });
  const { t } = useI18n();

  const summary = computed(() => ({
    General: {
      Name: t('settings.summary.windows.general.title'),
      items: [
        {
          Name: t('settings.summary.windows.general.network.title'),
          items: [
            {
              Name: t('settings.summary.windows.general.network.coordinatorPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.coordinator?.agentCommunicationPort,
            },
            {
              Name: t('settings.summary.windows.general.network.uiPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.ibManager?.ibManagerPort,
            },
            {
              Name: t('settings.summary.windows.general.network.serverPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.coordinator?.restServicePort,
            },
            {
              Name: t('settings.summary.windows.general.network.licensePort.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.license?.licenseServerPort,
            },
            {
              Name: t('settings.summary.windows.general.network.rabbitMqPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.ibManager?.rabbitMqPort,
            },
            {
              Name: t('settings.summary.windows.general.network.connectivity.text'),
              Val: inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.agent?.reportInterval, 'timeForm', inputsSelectedOption.value.ReportInterval),
              addedLabel: inputsWithNumMap[inputsSelectedOption.value.ReportInterval],
            },
            {
              Name: t('settings.summary.windows.general.network.prometheusIntegration.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.system?.prometheusEnabled,
            },
            isLicenseContainAPIAccessFeature.value && result.value?.ibManagerEntities?.settings?.windows?.system?.prometheusEnabled
              ? {
                  Name: t('settings.summary.windows.general.network.prometheusPort.text'),
                  Val: result.value?.ibManagerEntities?.settings?.windows?.system?.prometheusPort,
                }
              : null,
            {
              Name: t('settings.summary.windows.general.network.encryption.text'),
              // Val: state.settingsFromCoord.AgentsUseSecureConnections,
              Val: result.value?.ibManagerEntities?.settings?.windows?.coordinator?.agentsUseSecureCommunication,
            },
            result.value?.ibManagerEntities?.settings?.windows?.coordinator?.agentsUseSecureCommunication
              ? {
                  Name: t('settings.summary.windows.general.network.SSLPort.text'),
                  Val: result.value?.ibManagerEntities?.settings?.windows?.coordinator?.agentCommunicationSSLPort,
                }
              : null,
            result.value?.ibManagerEntities?.settings?.windows?.coordinator?.agentsUseSecureCommunication
              ? {
                  Name: t('settings.summary.windows.general.network.SslCnValidationPattern.text'),
                  Val: !!result.value?.ibManagerEntities?.settings?.windows?.coordinator?.sslCNValidationPattern,
                }
              : null,
          ].filter(item => item),
        },
        {
          Name: t('settings.summary.windows.general.system.title'),
          items: [
            {
              Name: t('settings.summary.windows.general.system.preventSysStandby.text'),
              Val: !result.value?.ibManagerEntities?.settings?.windows?.system?.allowSystemStandby,

              // Val: !state.settingsFromCoord.AllowSystemStandby,
            }, // flip because everybody is crazy
            {
              Name: t('settings.summary.windows.general.system.dontShowWinFW.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.system?.ignoreFirewall,
            },
            {
              Name: t('settings.summary.windows.general.system.helpUsImprove.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.system?.telemetryEnabled,
            },
          ],
        },
        {
          Name: t('settings.summary.windows.general.coreLimit.title'),
          items: [
            {
              Name: t('settings.summary.windows.general.coreLimit.forEachBuild.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.agent?.maxWorkers,
            },
          ],
        },
        {
          Name: t('settings.summary.windows.general.userAuth.title'),
          items: [
            {
              Name: t('settings.summary.windows.general.userAuth.activeDirectory.text'),
              Val: adStatus.value,
            },
          ],
        },
        {
          Name: t('settings.summary.windows.general.backup.summarySection.title'),
          items: [
            {
              Name: t('settings.summary.windows.general.backup.summarySection.isBackupConfiguredValueTitle'),
              Val: result.value?.ibManagerEntities?.settings?.common?.backup?.otherCoordinatorHost,
            },
          ],
        },
      ],
    },
    Agents: {
      Name: t('settings.summary.windows.agents.title'),
      items: [
        {
          Name: t('settings.summary.windows.agents.performance.title'),
          items: [
            {
              Name: t('settings.summary.windows.agents.performance.diskSpace.text'),
              Val: inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.agent?.minDiskFree, 'disksize', inputsSelectedOption.value.MinDiskFree),
              addedLabel: inputsWithNumMap[inputsSelectedOption.value.MinDiskFree],
            },
            {
              Name: t('settings.summary.windows.agents.performance.virtualMemory.text'),
              Val: inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.agent?.minVirtualMemory, 'disksize', inputsSelectedOption.value.MinVirtualMem),
              addedLabel: inputsWithNumMap[inputsSelectedOption.value.MinVirtualMem],
            },
            {
              Name: t('settings.summary.windows.agents.performance.physicalMemory.text'),
              Val: inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.agent?.minPhysicalMemory, 'disksize', inputsSelectedOption.value.MinPhysicalMem),
              addedLabel: inputsWithNumMap[inputsSelectedOption.value.MinPhysicalMem],
            },
            {
              Name: t('settings.summary.windows.agents.performance.cpuUsage.text'),
              Val: Math.round(Number(result.value?.ibManagerEntities?.settings?.windows?.agent?.minIdleLevel)),
              Neon: true,
              addedLabel: ' %',
            },
            {
              Name: t('settings.summary.windows.agents.performance.ignoreProcesses.text'),
              Val: !!result.value?.ibManagerEntities?.settings?.windows?.agent?.ignoreProcesses,
            },
          ],
        },
        {
          Name: t('settings.summary.windows.agents.cache.title'),
          items: [
            {
              Name: t('settings.summary.windows.agents.cache.scheduleTime.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.agent?.runCacheCleanup
                ? inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.agent?.cacheCleanupInterval, 'timePeriod', inputsSelectedOption.value.CacheCleanupInterval)
                : null,
              addedLabel: inputsWithNumMap[inputsSelectedOption.value.CacheCleanupInterval],
            },
          ].filter(Boolean),
        },

        {
          Name: t('settings.agents.agentHelperPermissions.title'),
          items: [
            {
              Name: t('settings.agents.agentHelperPermissions.summaryTitle'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.agent?.allowEnableDisableAsHelper,
            },
          ],
        },
        {
          Name: t('settings.summary.windows.agents.license.title'),
          items: [
            {
              Name: t('settings.summary.windows.agents.license.defaultInitiatorLicense.text'),
              Val: setInitiatorType(
                result.value?.ibManagerEntities?.settings?.windows?.license?.defaultInitiatorType || License_RegistrationType.None,
                result.value?.ibManagerEntities?.settings?.windows?.license?.defaultCIInitiatorType || License_RegistrationType.None,
              ),
            },
            {
              Name: t('settings.summary.windows.agents.license.defaultHelperLicense.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.license?.defaultHelperType,
            },
            {
              Name: t('settings.summary.windows.agents.license.defaultHelperCoresAllocation.text'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.license?.helperCoresAssignmentPercentage,
              addedLabel: ' %',
            },
            shouldHideReleaseOfflineAgents.value
              ? null
              : {
                  Name: t('settings.summary.windows.agents.license.floatingReleaseTime.text'),
                  Val: inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.license?.floatingReleaseTime, 'timeForm', inputsSelectedOption.value.FloatingReleaseTime),
                  addedLabel: inputsWithNumMap[inputsSelectedOption.value.FloatingReleaseTime],
                },
            // todo - remove business logic from store
            isBuildAvoidanceLicensed(result?.value?.ibManagerEntities?.license?.windows?.summary)
              ? {
                  Name: t('settings.summary.windows.agents.license.baAllocation.text'),
                  Val: result.value?.ibManagerEntities?.settings?.windows?.license?.automaticBuildCacheLicense,
                }
              : null,
          ].filter(Boolean),
        },
        {
          Name: t('settings.agents.removeFloatingOfflineAgents.title'),
          items: [
            {
              Name: t('settings.agents.removeFloatingOfflineAgents.summaryText'),
              Val: result.value?.ibManagerEntities?.settings?.windows?.agent?.showOfflineFloatingAgents
                ? inputWithMenuFilter(result.value?.ibManagerEntities?.settings?.windows?.agent?.offlineFloatingAgentsPeriod, 'timePeriod', inputsSelectedOption.value.OfflineAgentsPeriod)
                : null,
              addedLabel: inputsWithNumMap[inputsSelectedOption.value.OfflineAgentsPeriod],
            },
          ],
        },
        // Temporarily removed until cache scheduling is implemented
        // {
        //   Name: t('settings.summary.windows.agents.cache.title'),
        //   items: [
        //     {
        //       Name: t('settings.summary.windows.agents.cache.scheduleTime.text'),
        //       Val: inputWithMenuFilter(state['settingsFromCoord'].CacheCleanupInterval, 'timePeriod', inputsSelectedOption.value.CacheCleanupInterval),
        //       addedLabel: inputsWithNumMap[inputsSelectedOption.value.CacheCleanupInterval],
        //     },
        //   ],
        // },
      ],
    },

    apiAccess: {
      Name: t('settings.summary.windows.apiAccess.title'),
      items: [
        {
          Name: t('settings.summary.windows.apiAccess.activeApiKeys.title'),
          Val: activeAPIKeys.value.length,
        },
      ],
    },
  }));
  return { summary };
};

export const useSettingsSummaryLinux = () => {
  const { t } = useI18n();
  const coordinatorsService = inject(IKCoordinatorsService);
  const { result } = useSettingsPageLinuxQuery(() => ({
    coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
  }));
  const { GQL_useKeysQuery } = useApiAccess();
  const activeAPIKeys = computed(() => GQL_useKeysQuery?.result?.value?.keys || []);
  const adStatus = computed(() => {
    const enabled = result.value?.ibManagerEntities?.settings?.common?.auth?.enabled;
    if (enabled) {
      return result.value?.ibManagerEntities?.settings?.common?.auth?.authenticationType === External_Authentication_Type.Ldap ? ActiveDirectoryStatus.adEnabled : ActiveDirectoryStatus.azureADEnabled;
    }
    return ActiveDirectoryStatus.disabled;
  });
  const summary = computed(() => ({
    General: {
      Name: t('settings.summary.linux.general.title'),
      items: [
        {
          Name: t('settings.summary.linux.general.system.title'),
          items: [
            {
              Name: t('settings.summary.linux.general.system.coresPerBuild.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.buildMaxCores,
            },
            {
              Name: t('settings.summary.linux.general.system.maxCPU.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.agentMaxCpuLoad,
            },
            {
              Name: t('settings.summary.linux.general.system.minMemory.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.agentMinFreeMemory,
            },
            {
              Name: t('settings.summary.linux.general.system.reloadTimeout.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.agentReloadTimeout,
            },
            {
              Name: t('settings.summary.linux.general.system.autoSubscribe.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.autoSubscribeNewAgents,
            },
            {
              Name: t('settings.summary.linux.general.system.telemetry.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.system?.telemetryEnabled,
            },
          ],
        },
        {
          Name: t('settings.summary.linux.general.backup.title'),
          items: [
            {
              Name: t('settings.summary.linux.general.backup.hostname.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.backup?.otherCoordinatorName,
            },
            {
              Name: t('settings.summary.linux.general.backup.port.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.backup?.otherCoordinatorPort,
            },
            {
              Name: t('settings.summary.linux.general.backup.timeout.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.backup?.backupCoordinatorTimeout,
            },
            {
              Name: t('settings.summary.linux.general.backup.syncFrequency.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.backup?.backupCoordinatorSyncFrequency,
            },
          ],
        },
        {
          Name: t('settings.summary.linux.general.network.title'),
          items: [
            {
              Name: t('settings.summary.linux.general.network.utilityPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.utilityPort,
            },
            {
              Name: t('settings.summary.linux.general.network.sessionPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.sessionPort,
            },
            {
              Name: t('settings.summary.linux.general.network.managerPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.common?.ibManager?.ibManagerPort,
            },

            {
              Name: t('settings.summary.linux.general.network.controlPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.coordinator?.agentControlPort,
            },
            {
              Name: t('settings.summary.linux.general.network.webserverPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.network?.coordinatorPort,
            },
            {
              Name: t('settings.summary.linux.general.network.webserverSSLPort.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.network?.coordinatorSSLPort,
            },
            {
              Name: t('settings.summary.linux.general.network.ipv6.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.network?.ipv6,
            },
          ],
        },
        {
          Name: t('settings.summary.linux.general.userAuth.title'),
          items: [
            {
              Name: t('settings.summary.linux.general.userAuth.activeDirectory.text'),
              Val: adStatus.value,
            },
          ],
        },
        {
          Name: t('settings.summary.linux.general.ssl.title'),
          items: [
            {
              Name: t('settings.summary.linux.general.ssl.sslValue.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.network?.ssl,
            },
            {
              Name: t('settings.summary.linux.general.ssl.useCoordinatorAgentTunnel.text'),
              Val: result.value?.ibManagerEntities?.settings?.linux?.network?.useCoordinatorAgentTunnel,
            },
          ].filter(item => item),
        },
        {
          Name: t('settings.summary.linux.apiAccess.title'),
          items: [
            {
              Name: t('settings.summary.linux.apiAccess.activeApiKeys.title'),
              Val: activeAPIKeys.value.length,
            },
          ],
        },
      ],
    },
  }));
  return { summary };
};

export const useSaveSettings = () => {
  const { mutate: GQL_updateSettings } = useSettings_Update_AsyncMutation({});
  const { mutate: mutateManagerPort } = useSettings_ChangeManagerPortMutation({});
  const { mutate: mutateCoordinatorServerPort } = useSettings_ChangeCoordinatorPort_AsyncMutation({});
  const coordinatorsService = inject(IKCoordinatorsService);

  /**
   * A function that takes a payload and returns a payload.
   * It takes an object with keys that match the SettingsResultPaths type, and returns an array of
   * Settings_SetRequest objects
   * @param data - Partial<Record<SettingsResultPaths, any>>
   */
  const payloadAdapter = (data: Partial<Record<SettingsResultPaths, any>>) =>
    (Object.keys(data) as Array<keyof typeof data>).reduce(
      (previousValue: Settings_SetRequest[], key) => [
        ...previousValue,
        {
          key: key
            .replace(/^settings.linux./, '')
            .replace(/^settings.windows./, '')
            .replace(/^settings.common./, ''),
          // String split check is for backup host value that is defined as number by Number.isNaN
          value: Number.isNaN(parseFloat(data[key])) || String(data[key]).split('.').length > 2 ? data[key] : Number(data[key]),
        },
      ],
      [],
    );

  const updateSettings = async (inputsDirty: InputsDirty) => {
    const data = (Object.keys(inputsDirty) as Array<keyof typeof inputsDirty>).reduce<Partial<Record<SettingsKeysHandledByRest, any>>>((previous, key) => {
      if (key === 'settings.common.ibManager.ibManagerPort' || key === 'settings.windows.coordinator.restServicePort') return previous;
      return {
        ...previous,
        [key]: inputsDirty[key],
      };
    }, {});
    const payload = payloadAdapter(data);
    if (payload.length) {
      const uuidResult = await GQL_updateSettings({
        configurations: {
          coordinatorId: coordinatorsService?.selectedCoordinator.value || '',
          settings: payload,
        },
      });
      const result = await longPolling.fetch('settings_update_asyncResult', uuidResult?.data?.settings_update_async);
      return result;
    }
  };

  /**
   * Updating the port number of the IB Manager.
   * It's a function that takes a number and returns a promise that resolves to a result
   * @param data - Record<Extract<SettingsResultPaths, 'settings.common.ibManager.ibManagerPort'>, number>
   * @returns The result of the mutation.
   */
  const updateIbManagerPort = async (data: Record<Extract<SettingsResultPaths, 'settings.common.ibManager.ibManagerPort'>, number>) => {
    const result = await mutateManagerPort({ port: data['settings.common.ibManager.ibManagerPort'] });
    return result;
  };

  /**
   * Updating the port number of the Coordinator Server Port.
   * It takes a number, and returns a promise that resolves to a result
   * @param data - Record<Extract<SettingsResultPaths, 'settings.windows.coordinator.restServicePort'>, number>
   * @returns The result of the mutation.
   */
  const updateCoordinatorServerPort = async (data: Record<Extract<SettingsResultPaths, 'settings.windows.coordinator.restServicePort'>, number>) => {
    const uuidResult = await mutateCoordinatorServerPort({ port: data['settings.windows.coordinator.restServicePort'] });
    const result = await longPolling.fetch('settings_changeCoordinatorPort_asyncResult', uuidResult?.data?.settings_changeCoordinatorPort_async);
    return result;
  };

  return { updateIbManagerPort, updateCoordinatorServerPort, updateSettings };
};

export const useAfterSettingsUpdate = () => {
  const { dialogPresets, closeDialog } = useDialogPresets();
  const appHealthCheck = inject(IKHealthCheck);
  const platformAuthentication = inject(IKPlatformAuthentication);

  const { toastMessage } = useNotify();
  const { t } = useI18n();
  const { checkUrl } = useHealthCheckService();

  const handleAfterSettingsUpdate = (result: Awaited<ReturnType<ReturnType<typeof useSaveSettings>['updateSettings']>>) => {
    if (result?.data.settings_update_asyncResult?.result) {
      result?.data.settings_update_asyncResult?.result.forEach(statusResult => {
        if (statusResult?.status === ResponseStatus.Ok) {
          return toastMessage(t('settings.notifications.settingsSavedSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.SUCCESS);
        }
        toastMessage(t('settings.notifications.settingsSavedUnSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
      });
    }
  };

  const handleAfterIbManagerPortUpdate = async (result: Awaited<ReturnType<ReturnType<typeof useSaveSettings>['updateIbManagerPort']>>) => {
    if (result?.data?.settings_changeManagerPort?.status === ResponseStatus.Ok) {
      dialogPresets.value.pleaseHold();
      appHealthCheck?.stop();
      const { protocol, hostname } = new URL(window.location.href);
      const healthCheckUrl =
        process.env.NODE_ENV === 'production'
          ? `${protocol}//${hostname}:${String(result.data?.settings_changeManagerPort?.port)}/${process.env.VUE_APP_IB_MANAGER_HEALTH_CHECK_PATHNAME}`
          : `${process.env.VUE_APP_IB_MANAGER_PROTOCOL}//${process.env.VUE_APP_IB_MANAGER_HOSTNAME}:${String(result.data?.settings_changeManagerPort?.port)}/${
              process.env.VUE_APP_IB_MANAGER_HEALTH_CHECK_PATHNAME
            }`;
      let retry = 0;
      const intervalHandler = setInterval(async () => {
        const isAlive = await checkUrl(healthCheckUrl, platformAuthentication?.getAccessToken() || '');
        if (isAlive) {
          clearInterval(intervalHandler);

          return dialogPresets.value.successSaveSettingsPage('DashboardPort', () => refreshPageAfterUIPortChange(result.data?.settings_changeManagerPort?.port));
        }
        if (++retry >= 30) {
          clearInterval(intervalHandler);
          closeDialog();
          toastMessage(t('settings.notifications.settingsSavedUnSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
        }
      }, 2000);
    }
  };

  const handleAfterCoordinatorServerPortUpdate = (result: Awaited<ReturnType<ReturnType<typeof useSaveSettings>['updateCoordinatorServerPort']>>) => {
    const response = result.data.settings_changeCoordinatorPort_asyncResult?.result;
    if (response?.status === ResponseStatus.Ok) {
      return toastMessage(t('settings.notifications.settingsSavedSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.SUCCESS);
    }
    toastMessage(t('settings.notifications.settingsSavedUnSuccessfully.text'), CONST_NOTIFY.MESSAGE_TYPE.ERROR);
  };

  return { handleAfterSettingsUpdate, handleAfterIbManagerPortUpdate, handleAfterCoordinatorServerPortUpdate };
};
